import { clearFilterStorage, removeAuthTokenCookie } from 'utility/helpers';
import clientApi from 'api/clientApi';
import { CONTACT_MESSAGE, Endpoints, SESSION_TIMEOUT_PATH } from 'utility/constants';
import { fetchOpCodes } from 'api/connection/opCodes';
import { getRoById } from 'redux/actions/member/ros';
import { MemberActionTypes } from 'redux/actions/member/actionTypes';
import { showErrorToast, showToast } from 'utility/toast';
import { store } from 'redux/storeConfig/store';

export * from 'redux/actions/member/actionTypes';
export * from 'redux/actions/member/customers';
export * from 'redux/actions/member/declined-service';
export * from 'redux/actions/member/images';
export * from 'redux/actions/member/messages';
export * from 'redux/actions/member/mpi';
export * from 'redux/actions/member/note';
export * from 'redux/actions/member/payments';
export * from 'redux/actions/member/platform-settings';
export * from 'redux/actions/member/quote';
export * from 'redux/actions/member/reconditioning-phase';
export * from 'redux/actions/member/ros';
export * from 'redux/actions/member/service-phase';
export * from 'redux/actions/member/support';
export * from 'redux/actions/member/uveye';
export * from 'redux/actions/member/video';

// TODO: Export these constants and import them in pages, reducer, endpoints, and filters to eliminate magic strings. Validate for regressions.
const REPORT_ADVISOR_RECOMMENDATION = 'advisorRecommendationReport';
const REPORT_ADVISOR = 'advisorReport';
const REPORT_ADVISOR_USAGE = 'advisorUsageReport';
const REPORT_CUSTOMER_ENGAGEMENT = 'customerEngagementReport';
const REPORT_MPI = 'mpiReport';
const REPORT_RECOMMENDATION = 'recommendationReport';
const REPORT_SERVICE_PHASE_CYCLE_TIME = 'servicePhaseCycleTime';
const REPORT_SERVICE_PHASE_TREND = 'servicePhaseTrendReport';
const REPORT_SERVICE_PHASE_USER_TREND = 'servicePhaseUserTrendReport';
const REPORT_TECHNICIAN_RECOMMENDATION = 'technicianRecommendationReport';
const REPORT_TECHNICIAN = 'technicianReport';
const REPORT_TECHNICIAN_USAGE = 'technicianUsageReport';
const REPORT_RECON_CYCLE_TIME = 'reconditioningPhaseCycleTime';
const REPORT_RECON_PHASE_TREND = 'vehicleReconditioningPhaseTrendReport';
const REPORT_RECON_USER_TREND = 'vehicleReconditioningUserTrendReport';

// in getRecords & pollRos
export const generateParams = (stateProp) => {
  const { currentPage, searchTerm, sortDirection, sortField } = store.getState().member;

  const params = {
    page: currentPage,
  };

  const filterGroup = localStorage.getItem('filterGroup') || 'shared';
  const items = JSON.parse(localStorage.getItem(`filterOptions-${filterGroup}`) || '[]');
  if (items?.length) {
    // Pull the filter objects out of the array and into params
    items.forEach((filter) => {
      if (String(filter.name).toLowerCase().includes('date')) {
        let startParamName = '';
        let endParamName = '';
        let endDateString;
        const todayISOString = new Date().toISOString();
        const start = String(filter.value[0] || todayISOString).slice(0, 10);

        if (filter.value.length > 0) {
          const endDate = new Date(filter.value[1]);
          const year = endDate.getFullYear();
          const month = String(endDate.getMonth() + 1).padStart(2, '0');
          const day = String(endDate.getDate()).padStart(2, '0');
          endDateString = `${year}-${month}-${day}`;
        }

        let end = endDateString || String(todayISOString).slice(0, 10);

        if (!end) {
          end = start;
        }
        if (stateProp === 'declinedServiceReport') {
          startParamName = filter.name === 'dateRange' ? 'closedDateStart' : filter.name + 'Start';
          endParamName = filter.name === 'dateRange' ? 'closedDateEnd' : filter.name + 'End';
        } else if (stateProp === 'demo') {
          startParamName = filter.name === 'dateRange' ? 'dateCreatedStart' : filter.name + 'Start';
          endParamName = filter.name === 'dateRange' ? 'dateCreatedEnd' : filter.name + 'End';
        } else {
          // Reporting specific: map named date ranges to explicit start/end parameter names
          if (filter.name === 'createdDateRange') {
            startParamName = 'createdStartDate';
            endParamName = 'createdEndDate';
          } else if (filter.name === 'closedDateRange') {
            startParamName = 'closedStartDate';
            endParamName = 'closedEndDate';
          } else if (filter.name === 'dateRange') {
            startParamName = 'startDate';
            endParamName = 'endDate';
          } else {
            startParamName = filter.name + 'Start';
            endParamName = filter.name + 'End';
          }
        }

        params[startParamName] = start + ' 00:00:00';
        params[endParamName] = end + ' 23:59:59';
        // Not get internal from storage and apply, so the app can get all values
      } else {
        params[filter.name] = filter.value;
      }
    });
  }

  if (searchTerm) {
    params.searchTerm = searchTerm;
  }

  if (sortField) {
    params.sortField = sortField;
    params.sortDirection = sortDirection;
  }

  return params;
};

// autocrud & dashboard
export const setFilters = (filters) => (dispatch) => {
  dispatch({ type: MemberActionTypes.SET_FILTERS, data: filters });
};

export const setLoading = (isLoading) => (dispatch) => {
  if (isLoading === true) dispatch({ type: MemberActionTypes.LOADING_START });
  if (isLoading === false) dispatch({ type: MemberActionTypes.LOADING_END });
};

export const setIsEditClicked = (isEditClicked) => (dispatch) => {
  if (isEditClicked === true) dispatch({ type: MemberActionTypes.CRUD_EDIT_OPEN });
  if (isEditClicked === false) dispatch({ type: MemberActionTypes.CRUD_EDIT_CLOSE });
};

export const handleError = (error) => {
  console.error(error);
  return (dispatch, getState) => {
    dispatch({ type: MemberActionTypes.HANDLE_ERROR, error });
    if (error?.response?.status === 401) {
      const state = getState();
      const { user } = state?.auth?.login ?? {};

      // user was logged in, but most likely their session expired
      if (user.id !== undefined) {
        const { pathname, search } = window.location;
        // remember the path so we can redirect back after login
        localStorage.setItem(SESSION_TIMEOUT_PATH, pathname + (search ?? ''));
      }

      // ISA-2402 (orchestrator triage, 2026-09-24): a session timeout ends the session without
      // ever calling signout(), so it must clear the same filter-storage keys signout() does --
      // otherwise the next person on a shared service-drive terminal inherits this user's
      // filtered view, including any Team selection. Deliberately NOT calling signout() itself:
      // that dispatches a full auth/member state reset from a shared error path used by every
      // 401 in the app, a blast radius well beyond this fix. The check-in Team pick
      // (clearCheckinTeam(), dispatched by signout()) needs no equivalent here -- it is pure
      // in-memory redux state (redux/reducers/appointments/index.ts has no persistence), and the
      // window.location.assign below is a full page reload that discards it along with the rest
      // of the JS runtime, unlike signout() which never reloads the page.
      clearFilterStorage();
      removeAuthTokenCookie();
      return window.location.assign('/login');
    } else if (error?.response?.status === 500) {
      return showErrorToast({ message: '500 Server Error' });
    } else {
      const objectContainingMessage = error?.response?.data?.message
        ? error.response.data
        : error?.message
          ? error
          : { message: `Something went wrong${CONTACT_MESSAGE}` };
      showErrorToast(objectContainingMessage);
    }
  };
};
export const setSelectedRow =
  ({ row }) =>
  (dispatch) => {
    // Stops payments from another RO blipping on screen before fresh load
    dispatch({ type: MemberActionTypes.CLEAR_PAYMENTS });
    dispatch({ type: MemberActionTypes.SET_SELECTED_ROW, data: row });
  };

// AutoCrud Actions
// autocrud, autoform, newRepairOrder, matrixModal
export const createRecord = (createdData, stateProp, params) => async (dispatch) => {
  dispatch({ type: MemberActionTypes.CREATE_RECORD });
  try {
    const response = await clientApi.post(Endpoints[stateProp], createdData);
    if (response.status === 200) {
      showToast({ message: 'Successfully created' });
      dispatch({ type: MemberActionTypes.RECORD_CREATED });

      // ROs needs separate CRUD
      if (stateProp === 'ros') {
        dispatch({ type: MemberActionTypes.RO_OUTDATED, forceUpdate: true });
      } else {
        dispatch(getRecords(stateProp, params));
      }
      return response;
    } else throw new Error(`Failed to create new record.${CONTACT_MESSAGE}`);
  } catch (error) {
    dispatch(handleError(error));
    return false;
  }
};

// used in dashboard, autocrud, mpiTemplate, autoform
export const deleteRecord = (deleted, stateProp, params) => async (dispatch) => {
  const { id } = deleted;
  dispatch({ type: 'DELETE_RECORD' });
  try {
    const response = await clientApi.delete(`${Endpoints[stateProp]}/${id}`);
    if (response.status === 200) {
      showToast({ message: 'Successfully deleted' });
      dispatch({ type: MemberActionTypes.RECORD_DELETED });
      // ROs needs separate CRUD
      if (stateProp === 'ros') {
        dispatch({ type: MemberActionTypes.RO_OUTDATED });
      } else {
        dispatch(getRecords(stateProp, params));
      }
      return true;
    } else {
      throw new Error(`Failed to delete record.${CONTACT_MESSAGE}`);
    }
  } catch (error) {
    dispatch(handleError(error));
    return false;
  }
};
export const addOpCode = (createdData, stateProp) => async (dispatch) => {
  dispatch({ type: MemberActionTypes.LOADING_START });
  try {
    const response = await clientApi.post(Endpoints[stateProp], createdData);
    if (response.status === 200) {
      dispatch({ type: MemberActionTypes.LOADING_END });
      showToast({ message: 'Successfully created' });
      return response.data;
    } else throw new Error(`Failed to create new record.${CONTACT_MESSAGE}`);
  } catch (error) {
    dispatch(handleError(error));
    return false;
  }
};
export const getMpiItemOpCode = (id, stateProp) => async (dispatch) => {
  try {
    dispatch({ type: MemberActionTypes.LOADING_START });
    const response = await clientApi.get(`${Endpoints[stateProp]}?mpiItemId=${id}`);
    if (response.status === 200) {
      dispatch({ type: MemberActionTypes.LOADING_END });
      return response.data;
    } else {
      throw new Error(`Failed to get OP codes.${CONTACT_MESSAGE}`);
    }
  } catch (error) {
    dispatch(handleError(error));
  }
};

export const deleteMpiItemOpCode = (id, stateProp) => async (dispatch) => {
  dispatch({ type: MemberActionTypes.LOADING_START });
  try {
    const response = await clientApi.delete(`${Endpoints[stateProp]}/${id}`);

    if (response.status === 200) {
      showToast({ message: 'Successfully deleted' });
      dispatch({ type: MemberActionTypes.LOADING_END });
      return true;
    } else {
      throw new Error(`Failed to delete OP codes.${CONTACT_MESSAGE}`);
    }
  } catch (error) {
    dispatch(handleError(error));
    return false;
  }
};

// autocrud
export const toggleCrudModal = (stateProp) => (dispatch) => {
  dispatch({ type: MemberActionTypes.TOGGLE_CRUD_MODAL, stateProp });
};

/**
 * `options.showSuccessToast` exists for writes whose verdict rides the RESPONSE BODY rather than
 * the HTTP status. ISA-2402's Team write is one: the DMS can reject it and the request is still a
 * 200, so the generic toast would tell the advisor it saved while `TeamWritebackAlert` tells them
 * it did not. It defaults to true so every other call site is untouched.
 */
export const updateRecord =
  (updated, stateProp, params, { showSuccessToast = true } = {}) =>
  async (dispatch) => {
    const { id } = updated;
    dispatch({ type: MemberActionTypes.UPDATE_RECORD });
    try {
      const response = await clientApi.put(`${Endpoints[stateProp]}/${id}`, updated);
      if (response.status === 200) {
        if (showSuccessToast) {
          showToast({ message: 'Successfully updated' });
        }
        dispatch({ type: MemberActionTypes.RECORD_UPDATED });
        if (stateProp === 'ros') {
          dispatch(getRoById(id));
        }
        dispatch(getRecords(stateProp, params));
        return response;
      } else {
        throw new Error(`Failed to update record.${CONTACT_MESSAGE}`);
      }
    } catch (error) {
      dispatch(handleError(error));
      return false;
    }
  };

export const getRecord = (stateProp, id) => async (dispatch) => {
  dispatch({ type: MemberActionTypes.GET_RECORDS, data: { stateProp } });
  try {
    const response = await clientApi.get(`${Endpoints[stateProp]}/${id}`);
    if (response.status === 200) {
      dispatch({
        type: MemberActionTypes.RECEIVE_RECORD,
      });
      return response.data;
    } else {
      throw new Error(`Failed to get records.${CONTACT_MESSAGE}`);
    }
  } catch (error) {
    dispatch(handleError(error));
    return false;
  }
};

/** @deprecated For ROS state props (where isRosStateProp() returns true), use getRos instead. */
export const getRecords =
  (stateProp, additionalParams, doNotReceive, forceClear) => async (dispatch) => {
    let params = generateParams(stateProp);
    if (additionalParams) {
      if ('priority' in additionalParams) {
        params.open = true;
        delete params.priority;
        delete additionalParams.priority;
      }
      params = Object.assign({}, params, additionalParams);
      // Remove any raw date-range keys that may have been passed via requestParams
      ['createdDateRange', 'closedDateRange', 'dateRange'].forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(params, k)) delete params[k];
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { pageLimit, ...requestParams } = additionalParams || {};

    if (stateProp === 'reportingCustomer' && Object.keys(requestParams).length === 0) {
      return false;
    }

    if (stateProp === REPORT_SERVICE_PHASE_TREND && !params.interval) {
      return false;
    }
    if (stateProp === REPORT_RECON_PHASE_TREND && !params.interval) {
      return false;
    }
    if (
      stateProp === REPORT_SERVICE_PHASE_USER_TREND &&
      (!params.interval || !params.servicePhaseId || (!params.role && !params.userIds))
    ) {
      return false;
    }
    if (stateProp === REPORT_SERVICE_PHASE_USER_TREND && params.servicePhaseId) {
      params.phaseId = params.servicePhaseId;
      delete params.servicePhaseId;
    }
    if (stateProp === REPORT_RECON_USER_TREND && params.reconditioningPhaseId) {
      params.phaseId = params.reconditioningPhaseId;
      delete params.reconditioningPhaseId;
    }
    if (
      stateProp === REPORT_RECON_USER_TREND &&
      (!params.interval || !params.phaseId || (!params.role && !params.userIds))
    ) {
      return false;
    }

    // `doNotReceive` performs a pure fetch (used by the full-dataset export) and must not
    // disturb the displayed grid: GET_RECORDS flips isLoading and would reset state, so skip it.
    if (!doNotReceive) {
      dispatch({
        type: MemberActionTypes.GET_RECORDS,
        stateProp: forceClear ? stateProp : undefined,
      });
    }

    let currentDate = new Date();
    if (
      [
        REPORT_ADVISOR_RECOMMENDATION,
        REPORT_ADVISOR,
        REPORT_ADVISOR_USAGE,
        REPORT_CUSTOMER_ENGAGEMENT,
        REPORT_MPI,
        REPORT_RECOMMENDATION,
        REPORT_SERVICE_PHASE_CYCLE_TIME,
        REPORT_SERVICE_PHASE_TREND,
        REPORT_TECHNICIAN_RECOMMENDATION,
        REPORT_TECHNICIAN,
        REPORT_TECHNICIAN_USAGE,
        REPORT_RECON_CYCLE_TIME,
        REPORT_RECON_PHASE_TREND,
        REPORT_RECON_USER_TREND,
      ].includes(stateProp) &&
      !params.startDate &&
      !params.endDate
    ) {
      params = {
        startDate: currentDate,
        endDate: new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate() - 30,
        ),
        metrics: additionalParams && additionalParams.metrics,
        role: additionalParams && additionalParams.role,
        ...params,
      };
    }
    if ('declinedServiceReport' === stateProp && !params.closedDateStart && !params.closedDateEnd) {
      params = {
        closedDateStart: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
        closedDateEnd: currentDate,
      };
    }

    try {
      const response = await clientApi.get(Endpoints[stateProp], { params });
      if (response.status === 200) {
        if (!doNotReceive) {
          dispatch({
            type: MemberActionTypes.RECEIVE_RECORDS,
            data: response.data,
            stateProp,
          });
        }

        return response;
      } else {
        throw new Error(`Failed to get records.${CONTACT_MESSAGE}`);
      }
    } catch (error) {
      dispatch(handleError(error));
      return false;
    }
  };

export const getPartsList = (repairOrderId) => async (dispatch) => {
  try {
    const response = await clientApi.get(
      `${Endpoints.partsPickList}?repairOrderId=${repairOrderId}`,
    );

    if (response.status === 200) {
      return response.data.results;
    } else throw new Error(`Failed to get parts list.${CONTACT_MESSAGE}`);
  } catch (error) {
    dispatch(handleError(error));
  }
};

export const updatePartsList = (status, partsListId) => async (dispatch) => {
  try {
    const action = status === 'Pick' ? 'picked' : 'picked-up';
    const response = await clientApi.post(`${Endpoints.partsPickList}/${action}`, {
      repairOrderPickListPartId: partsListId,
    });

    if (response.status === 200) {
      return response.data;
    } else throw new Error(`Failed to update parts list.${CONTACT_MESSAGE}`);
  } catch (error) {
    dispatch(handleError(error));
  }
};

export const getOpCodes = (params) => async (dispatch) => {
  try {
    const response = await clientApi.get(Endpoints.opCodes, {
      params,
    });
    if (response.status === 200) {
      dispatch({ type: MemberActionTypes.RECEIVE_OP_CODES, data: response.data });
      return response;
    } else throw new Error(`Failed to get Op. Codes.${CONTACT_MESSAGE}`);
  } catch (error) {
    dispatch(handleError(error));
    return [];
  }
};

export const closePeModal = (value) => (dispatch) => {
  dispatch({ type: MemberActionTypes.CLOSE_PEMODAL, data: { isPesafModalClosed: value } });
};
// dashboard
export const getUsers = (params) => async (dispatch) => {
  try {
    const response = await clientApi.get(Endpoints['users'], {
      params,
    });
    if (response.status === 200) {
      return response;
    } else throw new Error(`Failed to get users.${CONTACT_MESSAGE}`);
  } catch (error) {
    dispatch(handleError(error));
    return [];
  }
};

const getFactoryMaintenanceOpCode = () => async (dispatch, getState) => {
  try {
    const { factoryMaintenanceOpCode } = getState().member.settings;
    const searchTerm = factoryMaintenanceOpCode || 'MISC';
    const opCodes = await fetchOpCodes({ searchTerm });

    const opCode = opCodes.results.find((opCode) => opCode.code === searchTerm);

    if (opCode) {
      dispatch({
        type: MemberActionTypes.SET_STATE,
        key: 'factoryMaintenanceOpCode',
        value: opCode,
      });
    }
  } catch (error) {
    dispatch(handleError(error));
  }
};

//Settings
export const getSettings = () => async (dispatch, getState) => {
  const { user } = getState().auth.login;
  if (!user.role) return;

  dispatch({ type: MemberActionTypes.GET_SETTINGS });
  try {
    const response = await clientApi.get(Endpoints.settings);
    if (response.status === 200) {
      dispatch({ type: MemberActionTypes.RECEIVE_SETTINGS, data: response.data });

      dispatch(getFactoryMaintenanceOpCode());
    } else {
      throw new Error(`Failed to get settings.${CONTACT_MESSAGE}`);
    }
  } catch (error) {
    return dispatch(handleError(error));
  }
};

export const getPriceMatrix = () => async (dispatch) => {
  dispatch({ type: MemberActionTypes.GET_PRICE_MATRIX });

  try {
    const response = await clientApi.get(Endpoints.priceMatrix);

    if (response.status === 200) {
      dispatch({
        type: MemberActionTypes.RECEIVE_PRICE_MATRIX,
        data: response.data || [],
      });
    } else {
      throw new Error(`Failed to get price matrix.${CONTACT_MESSAGE}`);
    }
  } catch (error) {
    return dispatch(handleError(error));
  }
};

export const updateAccountSecurityQuestion =
  ({ id, securityAnswer: answer, securityQuestion: question }) =>
  async (dispatch) => {
    try {
      const response = await clientApi.patch(`${Endpoints.security}/${id}/set`, {
        answer,
        question,
      });
      if (response.status === 200) {
        return showToast({
          message: 'Security question and answer successfully updated',
        });
      }
    } catch (error) {
      return dispatch(handleError(error));
    }
  };
// dashboard
export const resendWaiver = (repairOrderId) => async (dispatch) => {
  dispatch({ type: MemberActionTypes.RESEND_WAIVER });
  try {
    const response = await clientApi.post(Endpoints.resendWaiver, {
      repairOrderId,
    });
    if (response.status === 200) {
      showToast({ message: 'Successfully sent waiver' });
      dispatch({ type: MemberActionTypes.WAIVER_SENT });
    }
  } catch (error) {
    return dispatch(handleError(error));
  }
};

export const updateSettings = (settings, fields, bypassError) => async (dispatch) => {
  dispatch({ type: MemberActionTypes.UPDATE_SETTINGS });

  for (let key in settings) {
    const field = fields.find((field) => field.selector === key);
    if (field?.type === 'checkbox' || field?.type === 'switch') {
      settings[key] =
        settings[key] === true || settings[key] === 'true' || settings[key] === '1' ? '1' : '0';
    }
  }

  try {
    const response = await clientApi.post(Endpoints.settings, settings);
    if (response.status === 200) {
      showToast({ message: 'Settings updated' });
      dispatch(getSettings());
    } else {
      if (!bypassError) throw new Error(`Failed to update settings.${CONTACT_MESSAGE}`);
      showErrorToast({ message: `Failed to update settings.${CONTACT_MESSAGE}` });
    }
    return response;
  } catch (error) {
    return dispatch(handleError(error));
  }
};
// autocrud
export const updateTableParameters = (params) => async (dispatch) => {
  dispatch({ type: MemberActionTypes.UPDATE_TABLE_PARAMETER, params });
};

export const setOpenModals = (name) => (dispatch, getState) => {
  // If the selected RO was updated, get ROs as well to ensure they're in sync
  const { selectedRow, ros } = getState().member;
  const matchingRO = ros.results?.find((ro) => ro.id === selectedRow?.id);
  if (!name && matchingRO !== selectedRow) {
    dispatch({ type: MemberActionTypes.RO_OUTDATED });
  }
  if (name.length > 0) {
    dispatch({ type: MemberActionTypes.SET_OPEN_DASHBOARD_MODAL, data: [name] });
  } else {
    dispatch({ type: MemberActionTypes.SET_OPEN_DASHBOARD_MODAL, data: [] });
  }
};

export const fnOpenModal = (name) => (dispatch, getState) => {
  const { openModals } = getState().member;
  dispatch({ type: MemberActionTypes.SET_OPEN_DASHBOARD_MODAL, data: [...openModals, name] });
};

export const fnCloseModal = (name) => (dispatch, getState) => {
  const { openModals } = getState().member;
  dispatch({
    type: MemberActionTypes.SET_OPEN_DASHBOARD_MODAL,
    data: openModals.filter((item) => item !== name),
  });
};

export const setprintableDoc = (data) => (dispatch) => {
  dispatch({ type: MemberActionTypes.SET_PRINTABLE_DOC_DATA, data });
};

export const toggleChatModal = () => (dispatch) => {
  dispatch({ type: MemberActionTypes.TOGGLE_CHAT_MODAL });
};

export const setChatInputValue = (data) => (dispatch) => {
  dispatch({ type: MemberActionTypes.SET_CHAT_INPUT_VALUE, data });
};

export const updateMessageType = (type) => (dispatch) => {
  dispatch({ type: MemberActionTypes.SET_MESSAGE_TYPE, data: type });
};
